import { z } from 'zod/v4';

import {
    CategoryIdSchema,
    ComponentCurrencySchema,
    ComponentFiltersSchema,
    ComponentMetricIdSchema,
    snapshotReadResultSchema,
} from '../components/schemas';

export const ComponentSavingsScenarioSchema = z.object({
    baseline: ComponentMetricIdSchema,
    comparison: ComponentMetricIdSchema,
});
export type ComponentSavingsScenario = z.infer<typeof ComponentSavingsScenarioSchema>;

export const ComponentKpiSavingsInputSchema = z.object({
    snapshotId: z.string().min(1).max(100),
    categoryId: CategoryIdSchema.optional(),
    scenario: ComponentSavingsScenarioSchema,
    filters: ComponentFiltersSchema,
});
export type ComponentKpiSavingsInput = z.infer<typeof ComponentKpiSavingsInputSchema>;

const Count = z.number().int().nonnegative();
const Amount = z.number().finite().nonnegative();
const Share = z.number().finite().nonnegative().nullable();
export const ComponentSavingsPointSchema = z.object({
    ipn: z.string(),
    description: z.string().nullable(),
    baselineSpend: Amount,
    comparisonSpend: Amount,
    opportunity: Amount,
});
export type ComponentSavingsPoint = z.infer<typeof ComponentSavingsPointSchema>;
/** Eventually consistent stored-period reads; status does not imply an atomic snapshot. */
export const ComponentKpiSavingsDataSchema = z.object({
    snapshotId: z.string(),
    scenario: ComponentSavingsScenarioSchema,
    currency: ComponentCurrencySchema,
    kpis: z.object({
        wellPricedCount: Count,
        wellPricedShare: Share,
        procurementSavings: Amount.nullable(),
        comparableCount: Count,
        opportunity: Amount.nullable(),
        opportunityShare: Share,
        aboveBaselineCount: Count,
    }),
    coverage: z.object({ totalCount: Count, comparableCount: Count, excludedCount: Count }),
    topOpportunities: z.array(ComponentSavingsPointSchema).max(10),
    scatter: z.object({
        points: z.array(ComponentSavingsPointSchema).max(1000),
        totalEligibleCount: Count,
        returnedCount: Count,
        sampled: z.boolean(),
    }),
});
export const ComponentKpiSavingsOutputSchema = snapshotReadResultSchema(ComponentKpiSavingsDataSchema);
export type ComponentKpiSavingsData = z.infer<typeof ComponentKpiSavingsDataSchema>;
export type ComponentKpiSavingsOutput = z.infer<typeof ComponentKpiSavingsOutputSchema>;

export const ComponentChartsSavingsInputSchema = ComponentKpiSavingsInputSchema;
export type ComponentChartsSavingsInput = z.infer<typeof ComponentChartsSavingsInputSchema>;
export const SourceCoverageSchema = z.discriminatedUnion('status', [
    z.object({ status: z.literal('supported'), availableCount: Count, share: z.number().min(0).max(1).nullable() }),
    z.object({ status: z.literal('unsupported'), availableCount: z.null(), share: z.null() }),
]);
export type SourceCoverage = z.infer<typeof SourceCoverageSchema>;
export const ComponentChartsSavingsDataSchema = z.object({
    snapshotId: z.string(),
    version: z.number().int().positive(),
    currency: ComponentCurrencySchema,
    history: z
        .array(
            z.object({
                effectiveAt: z.iso.datetime(),
                stored: z.boolean(),
                totalCount: Count,
                comparableCount: Count,
                baselineSpend: Amount.nullable(),
                comparisonSpend: Amount.nullable(),
                demandQuantity: z.number().finite().nonnegative().nullable().optional(),
            }),
        )
        .max(52),
    quality: z.object({
        totalCount: Count,
        matchedParts: SourceCoverageSchema,
        demand: SourceCoverageSchema,
        baselinePrice: SourceCoverageSchema,
        comparisonPrice: SourceCoverageSchema,
    }),
});
export type ComponentChartsSavingsData = z.infer<typeof ComponentChartsSavingsDataSchema>;
export const ComponentChartsSavingsOutputSchema = snapshotReadResultSchema(ComponentChartsSavingsDataSchema);
export type ComponentChartsSavingsOutput = z.infer<typeof ComponentChartsSavingsOutputSchema>;
